import { FC, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

// Local Imports
import RiskProfileResult from '../components/Questionnaire/RiskProfileResult'
import { useRiskQuestionnaireStore } from '../state-management/questionnaireStore'
import { trackEvent, updateAppStatusBar } from '../utils/appBridge'
import { EventNames } from '../types/misc.types'
import { saveRiskProfile } from '../repository/rebalance'

// --- Loading and Error Components ---
const LoadingState = ({ message }: { message: string }) => (
  <div className='min-h-screen bg-gray-100 flex items-center justify-center'>
    <div className='text-center'>
      <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
      <p className='text-gray-600'>{message}</p>
    </div>
  </div>
)

const ErrorState = ({ message }: { message: string }) => (
  <div className='min-h-screen bg-gray-100 flex items-center justify-center'>
    <div className='text-center p-4 bg-red-100 border border-red-400 text-red-700 rounded'>
      <p className='font-bold'>An error occurred</p>
      <p>{message}</p>
    </div>
  </div>
)

const RiskProfileResultPage: FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const { riskScore, riskProfile, answers } = useRiskQuestionnaireStore()

  useEffect(() => {
    updateAppStatusBar('FFFFFF', 'dark')
  }, [])

  // ## 1. Setup the Mutation
  // This hook handles the POST request to save the user's risk profile.
  const { mutate, isPending, isError } = useMutation<AxiosResponse, Error, { riskScore: number; riskProfile: string }>({
    mutationFn: ({ riskProfile, riskScore }) => {
      // Create the request body using stored answers
      const requestBody: any = {
        resultantRiskProfile: riskProfile.toLocaleUpperCase().split(' ').join('_'),
        riskScore: riskScore
      }

      // Add questions and answers from stored data
      answers.forEach((answer, index) => {
        const questionKey = `question${index + 1}` as keyof typeof requestBody
        const answerKey = `answer${index + 1}` as keyof typeof requestBody
        requestBody[questionKey] = answer.questionText
        requestBody[answerKey] = answer.optionText
      })

      return saveRiskProfile(requestBody)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rebalance-plan-summary'] })
      // This side-effect now runs only after the API call is successful.
      console.log('Risk profile saved successfully.')
      trackEvent({
        eventName: EventNames.ScreenLoaded,
        payload: {
          screenType: 'RISK_PROFILE_RESULT_PAGE',
          ScreenName: riskProfile?.toLocaleUpperCase().split(' ').join('_') || ''
        }
      })
    },
    onError: (error) => {
      // Handle any errors from the API call
      console.error('Failed to save risk profile:', error.message)
    }
  })

  // ## 2. Add Guard Clause
  // This effect redirects the user if they land on this page without completing the questionnaire.
  // It runs before the mutation is triggered.
  useEffect(() => {
    if (!riskProfile || !riskScore || answers.length === 0) {
      console.warn('No risk profile data found, redirecting to questionnaire.')
      navigate(`/rebalance-questionnaire${from ? `?from=${from}` : ''}`)
    }
  }, [riskProfile, riskScore, answers, navigate, from])

  // ## 3. Trigger the Mutation
  // This effect runs once the component has the necessary data (riskScore, riskProfile).
  useEffect(() => {
    if (riskScore && riskProfile && answers.length > 0) {
      mutate({ riskScore, riskProfile })
    }
  }, [riskScore, riskProfile, answers, mutate])

  // ## 4. Handle Loading and Error States
  // If the store data is missing, show a generic loading state while the redirect happens.
  if (!riskProfile || !riskScore || answers.length === 0) {
    return <LoadingState message='Loading your profile...' />
  }

  // While the API call is in progress, show a specific saving message.
  if (isPending) {
    return <LoadingState message='Saving your risk profile...' />
  }

  // If the API call fails, show an error message.
  if (isError) {
    return <ErrorState message="We couldn't save your results. Please try again later." />
  }

  // ## 5. Render Success State
  // Once the mutation is successful, display the results.
  return (
    <div className='min-h-screen bg-gray-100 flex items-center justify-center'>
      <RiskProfileResult from={from} score={riskScore} profile={riskProfile} navigate={navigate} />
    </div>
  )
}

export default RiskProfileResultPage
